<template>
    <div>
      <el-form ref="formRef" :model="form" label-width="120px">
        <!-- Searchable Country Selector -->
        <el-form-item label="Country" :rules="countryRules" prop="countryCode">
          <el-select
            v-model="form.countryCode"
            filterable
            remote
            remote-method="filterCountry"
            placeholder="Search by country name or code"
          >
            <el-option
              v-for="(country, index) in filteredCountries"
              :key="index"
              :label="`${country.name} (${country.code})`"
              :value="country.code"
            />
          </el-select>
        </el-form-item>
  
        <!-- Phone Number Input -->
        <el-form-item label="Phone Number" :rules="phoneRules" prop="phoneNumber">
          <el-input
            v-model="form.phoneNumber"
            placeholder="Enter phone number"
            @blur="prependCountryCode"
          />
        </el-form-item>
  
        <!-- Submit Button -->
        <el-button type="primary" @click="submitForm">Submit</el-button>
      </el-form>
    </div>
  </template>
  
  <script>
  import { ref } from 'vue';
  import { ElForm, ElFormItem, ElInput, ElButton, ElSelect, ElOption } from 'element-plus';
  import { parsePhoneNumberFromString } from 'libphonenumber-js';
  
  export default {
    components: {
      ElForm,
      ElFormItem,
      ElInput,
      ElButton,
      ElSelect,
      ElOption,
    },
    setup() {
      const formRef = ref(null);
      const form = ref({
        countryCode: 'US',  // Default country code key
        phoneNumber: '',
      });
  
      // Mapping country code keys (e.g., US, EG) to phone numbers with country codes (+1, +20)
      const countries = {
        EG: '+20', // Egypt
        US: '+1', // United States
        IN: '+91', // India
        GB: '+44', // United Kingdom
        FR: '+33', // France
        DE: '+49', // Germany
        CA: '+1', // Canada
        AU: '+61', // Australia
        IT: '+39', // Italy
        ES: '+34', // Spain
        BR: '+55', // Brazil
        MX: '+52', // Mexico
        ZA: '+27', // South Africa
        // Add more countries here...
      };
  
      // Reverse mapping from country codes (+1, +20, etc.) to country keys (US, EG, etc.)
      const countryCodeMap = Object.entries(countries).reduce((acc, [key, value]) => {
        acc[value] = key;
        return acc;
      }, {});
  
      // Rules for phone number and country selection
      const phoneRules = [
        {
          validator: async (rule, value) => {
            if (!value) return Promise.reject('Phone number is required');
            
            // Prepend the selected country code before validation
            const phoneNumber = parsePhoneNumberFromString(value, form.value.countryCode);
            if (phoneNumber && phoneNumber.isValid()) {
              return Promise.resolve();
            } else {
              return Promise.reject('Invalid phone number');
            }
          },
          trigger: 'blur',
        },
      ];
  
      const countryRules = [
        { required: true, message: 'Please select a country', trigger: 'change' },
      ];
  
      // Create an array of countries with name and code
      const countryOptions = ref(
        Object.entries(countries).map(([code, countryCode]) => ({
          name: code, // Country name (like 'US', 'EG')
          code: countryCode, // Country code (+1, +20)
        }))
      );
  
      const filterCountry = (query) => {
        if (!query) {
          filteredCountries.value = countryOptions.value;
          return;
        }
        const lowerQuery = query.toLowerCase();
        filteredCountries.value = countryOptions.value.filter(({ name, code }) => {
          return name.toLowerCase().includes(lowerQuery) || code.includes(query);
        });
      };
  
      const filteredCountries = ref(countryOptions.value);
  
      // Automatically prepend the country code to the phone number on blur (when the input loses focus)
      const prependCountryCode = () => {
        const countryCode = countries[form.value.countryCode]; // This should be the correct country dialing code
  
        // Check if the country code exists in countries object
        if (!countryCode) {
          console.error('Invalid country code!');
          return; 
        }
  
        // If the phone number is not already prepended with the country code
        if (form.value.phoneNumber && !form.value.phoneNumber.startsWith(countryCode)) {
          form.value.phoneNumber = countryCode + form.value.phoneNumber;
        }
      };
  
      // Submit form function
      const submitForm = () => {
        formRef.value.validate((valid) => {
          if (valid) {
            alert('Form submitted!');
          } else {
            console.log('Form validation failed');
          }
        });
      };
  
      return {
        form,
        countryOptions,
        phoneRules,
        countryRules,
        filteredCountries,
        filterCountry,
        prependCountryCode,
        submitForm,
        formRef,
      };
    },
  };
  </script>
  
  <style scoped>
  /* Add any custom styles here */
  </style>
  